import apiClient from './api';
import { ApiResponse } from '../types';

export interface Integration {
  id: string;
  organization_id: string;
  name: string;
  platform_type: 'woocommerce' | 'shopify' | 'custom_api';
  credentials: {
    store_url?: string;
    consumer_key?: string;
    consumer_secret?: string;
    access_token?: string;
  };
  settings: Record<string, unknown>;
  status: 'connected' | 'syncing' | 'error' | 'disconnected';
  knowledge_base_id?: string;
  last_synced_at?: string;
  sync_error?: string;
  item_counts?: Record<string, number>;
  created_at: string;
  updated_at: string;
}

export interface IntegrationItem {
  id: string;
  integration_id: string;
  external_id: string;
  item_type: string;
  data: Record<string, unknown>;
  kb_document_id?: string;
  last_synced_at: string;
  created_at: string;
  updated_at: string;
}

export interface PaginatedItems {
  items: IntegrationItem[];
  meta: {
    nextCursor: string | null;
    total: number;
    limit: number;
  };
}

export interface ExportRecord {
  id: string;
  organization_id: string;
  entity_type: string;
  format: 'csv' | 'json' | 'jsonl';
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  filters: Record<string, unknown>;
  file_key?: string;
  file_size?: number;
  total_rows: number;
  processed_rows: number;
  error_message?: string;
  created_by?: string;
  started_at?: string;
  completed_at?: string;
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

class IntegrationsService {
  // --- Integrations ---

  async getIntegrations(): Promise<Integration[]> {
    const response = await apiClient.get<ApiResponse<Integration[]>>('/integrations');
    return response.data.data || [];
  }

  async getIntegration(id: string): Promise<Integration> {
    const response = await apiClient.get<ApiResponse<Integration>>(`/integrations/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Integration not found');
  }

  async createCustomIntegration(name: string, description?: string): Promise<Integration> {
    const response = await apiClient.post<ApiResponse<Integration>>('/integrations/custom', {
      name,
      description,
    });
    if (response.data.data) return response.data.data;
    throw new Error((response.data as any).error?.message || 'Failed to create custom integration');
  }

  async connectStore(platform: string, credentials: Record<string, string>, name?: string): Promise<Integration> {
    const response = await apiClient.post<ApiResponse<Integration>>(`/integrations/${platform}/connect`, {
      ...credentials,
      name,
    });
    if (response.data.data) return response.data.data;
    throw new Error((response.data as any).error?.message || 'Failed to connect store');
  }

  async disconnectIntegration(id: string): Promise<void> {
    await apiClient.delete(`/integrations/${id}`);
  }

  async syncIntegration(id: string): Promise<void> {
    await apiClient.post(`/integrations/${id}/sync`);
  }

  // --- Items ---

  async getItems(
    integrationId: string,
    params: { type?: string; cursor?: string; limit?: number }
  ): Promise<PaginatedItems> {
    const query = new URLSearchParams();
    if (params.type) query.set('type', params.type);
    if (params.cursor) query.set('cursor', params.cursor);
    if (params.limit) query.set('limit', String(params.limit));

    const response = await apiClient.get(`/integrations/${integrationId}/items?${query.toString()}`);
    return {
      items: response.data.data || [],
      meta: response.data.meta || { nextCursor: null, total: 0, limit: 50 },
    };
  }

  // --- Exports ---

  async getExports(): Promise<ExportRecord[]> {
    const response = await apiClient.get<ApiResponse<ExportRecord[]>>('/exports');
    return response.data.data || [];
  }

  async createExport(data: {
    entity_type: string;
    format: string;
    filters?: Record<string, unknown>;
  }): Promise<ExportRecord> {
    const response = await apiClient.post<ApiResponse<ExportRecord>>('/exports', data);
    if (response.data.data) return response.data.data;
    throw new Error((response.data as any).error?.message || 'Failed to create export');
  }

  async getExportStatus(id: string): Promise<ExportRecord> {
    const response = await apiClient.get<ApiResponse<ExportRecord>>(`/exports/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Export not found');
  }

  async downloadExport(id: string): Promise<Blob> {
    const response = await apiClient.get(`/exports/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async deleteExport(id: string): Promise<void> {
    await apiClient.delete(`/exports/${id}`);
  }
}

export const integrationsService = new IntegrationsService();
